import { cn } from '../cn.js';
import { useUiLabels } from '../labels.js';

const palette = [
  'bg-gradient-to-br from-rose-400 to-rose-600',
  'bg-gradient-to-br from-orange-400 to-orange-600',
  'bg-gradient-to-br from-amber-400 to-amber-600',
  'bg-gradient-to-br from-lime-500 to-lime-700',
  'bg-gradient-to-br from-emerald-400 to-emerald-600',
  'bg-gradient-to-br from-teal-400 to-teal-600',
  'bg-gradient-to-br from-sky-400 to-sky-600',
  'bg-gradient-to-br from-indigo-400 to-indigo-600',
  'bg-gradient-to-br from-violet-400 to-violet-600',
  'bg-gradient-to-br from-fuchsia-400 to-fuchsia-600',
];

/** Stable colour per id so a person always gets the same avatar colour. */
export function avatarColor(seed: string): string {
  let hash = 0;
  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return palette[Math.abs(hash) % palette.length] ?? 'bg-slate-500';
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  const letters = parts.length > 1 ? [first[0], parts.at(-1)?.[0]] : [first[0], first[1]];
  return letters.filter(Boolean).join('').toUpperCase() || '?';
}

const sizes = {
  xs: 'size-5 text-[9px]',
  sm: 'size-7 text-[11px]',
  md: 'size-9 text-sm',
  lg: 'size-16 text-xl',
  xl: 'size-28 text-4xl',
} as const;

export interface AvatarProps {
  id: string;
  name: string;
  src?: string | null;
  size?: keyof typeof sizes;
  /** Shows a presence dot when defined. */
  online?: boolean;
  /** People are circles, teams/workspaces are rounded squares. */
  shape?: 'circle' | 'square';
  className?: string;
}

export function Avatar({
  id,
  name,
  src,
  size = 'md',
  online,
  shape = 'circle',
  className,
}: AvatarProps) {
  const labels = useUiLabels();
  const radius =
    shape === 'circle'
      ? 'rounded-full'
      : size === 'lg' || size === 'xl'
        ? 'rounded-2xl'
        : 'rounded-lg';
  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      {src ? (
        <img src={src} alt={name} className={cn('object-cover', radius, sizes[size])} />
      ) : (
        <span
          aria-hidden
          className={cn(
            'inline-flex items-center justify-center font-semibold tracking-tight text-white select-none',
            radius,
            sizes[size],
            avatarColor(id),
          )}
        >
          {initials(name)}
        </span>
      )}
      {online !== undefined && (
        <span
          aria-label={online ? labels.online : labels.offline}
          className={cn(
            'absolute -right-0.5 -bottom-0.5 size-2.5 rounded-full ring-2 ring-[var(--avatar-ring,var(--color-surface))]',
            online ? 'bg-success' : 'bg-subtle',
          )}
        />
      )}
    </span>
  );
}

/** Overlapping avatars for a list of people (e.g. task assignees). */
export function AvatarStack({
  people,
  max = 3,
  size = 'xs',
}: {
  people: { id: string; displayName: string; avatarUrl: string | null }[];
  max?: number;
  size?: 'xs' | 'sm';
}) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <span
      className="flex items-center -space-x-1.5"
      title={people.map((p) => p.displayName).join(', ')}
    >
      {shown.map((person) => (
        <Avatar
          key={person.id}
          id={person.id}
          name={person.displayName}
          src={person.avatarUrl}
          size={size}
          className="rounded-full ring-2 ring-[var(--avatar-ring,var(--color-surface))]"
        />
      ))}
      {rest > 0 && <span className="pl-2.5 text-xs text-muted">+{rest}</span>}
    </span>
  );
}
